/**
 * Storefront event listeners. Reads page/item JSON emitted by Liquid and calls
 * jmAnalytics.track. Ticket 1's analytics.js runs first (plain module script) and
 * defines window.jmAnalytics; this module imports the same track() for tree-shaking
 * friendliness and to guarantee load order via the importmap.
 */
import { track } from '@theme/analytics';
import { readJSON } from '@theme/analytics-utils';
import { applyAttribution, originFor } from '@theme/analytics-attribution';

const PAGE_KEY = 'jm:lastUrl';

/** Fire custom_page_view once per page load. */
function firePageView() {
  const page = readJSON('analytics-page'); // { page_type, currency }
  let oldUrl = 'first';
  try {
    oldUrl = sessionStorage.getItem(PAGE_KEY) || 'first';
  } catch (error) {
    oldUrl = document.referrer || 'first';
  }
  const newUrl = window.location.href;

  track('custom_page_view', {
    old_url: oldUrl,
    new_url: newUrl,
    page_title: document.title,
    page_type: page.page_type || 'other',
  });

  try {
    sessionStorage.setItem(PAGE_KEY, newUrl);
  } catch (error) {
    /* storage unavailable — non-fatal */
  }
}

/** Fire view_item once on product detail pages. */
function fireViewItem() {
  const page = readJSON('analytics-page');
  if (page.page_type !== 'product_detail_page') return;

  const el = document.querySelector('script.analytics-item[data-analytics-context="main"]');
  if (!el || !el.textContent) return;

  let item;
  try {
    item = JSON.parse(el.textContent);
  } catch (error) {
    return;
  }

  // 'main' emits a single item for the selected-or-first variant; tolerate an
  // array (take the first) for resilience.
  if (Array.isArray(item)) item = item[0];
  if (!item) return;

  // The list this product was clicked from, recorded by analytics-attribution.js
  // on the previous page. 'direct' when the PDP was not reached through a list.
  const productId = el.getAttribute('data-product-id');
  const variantId = el.getAttribute('data-variant-id');
  applyAttribution(item, productId, variantId);

  // value = the emitted item's price (single product being viewed).
  track('view_item', {
    ecommerce: {
      value: Number(item.price || 0),
      currency: page.currency,
      origin: originFor(productId, variantId),
      items: [item],
    },
  });
}

function init() {
  firePageView();
  fireViewItem();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
